import { ArrowLeft, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

export interface AttemptListItem {
  _id: string;
  type: string;
  category?: string;
  subcategory?: string;
  score: number;
  totalCorrect: number;
  totalWrong: number;
  totalSkipped: number;
  passed?: boolean;
  createdAt: string;
  mockTestId?: { _id: string; title?: { en?: string }; type?: string; isActive?: boolean } | null;
  userId?: { _id: string; profile?: { name?: string }; email?: string } | null;
}

export interface AttemptAnswer {
  questionId: string;
  selectedOptionId?: string;
  selectedOptionIndex?: number;
  isCorrect: boolean;
  timeSpentSeconds: number;
  audioPlayCount?: number;
}

export interface AttemptDetail extends AttemptListItem {
  answers: AttemptAnswer[];
}

export interface QuestionOption {
  _id: string;
  text?: string;
  format?: string;
  isCorrect: boolean;
}

export interface QuestionRef {
  _id: string;
  name?: string;
  category?: string;
  subcategory?: string;
  difficulty?: string;
  question?: { format?: string; text?: string; blocks?: unknown[] };
  options?: QuestionOption[];
}

export function testName(item: Pick<AttemptListItem, 'mockTestId' | 'type' | 'category'>): string {
  if (item.mockTestId?.title?.en) return item.mockTestId.title.en;
  if (item.type === 'quiz') {
    const cat = item.category ? item.category.charAt(0).toUpperCase() + item.category.slice(1) : '';
    return cat ? `${cat} Quiz` : 'Practice Quiz';
  }
  return 'UBT Test';
}

/** Per-question breakdown for a single attempt — shared by the per-user Activity
 * drawer and the per-test Scores drawer, since both drill into the same
 * `GET /users/:id/attempts/:attemptId` endpoint. */
export default function AttemptDetailView({
  loading,
  detail,
  onBack,
}: {
  loading: boolean;
  detail?: { attempt: AttemptDetail; questions: QuestionRef[] };
  onBack: () => void;
}) {
  if (loading || !detail) {
    return <p className="py-8 text-center text-gray-400">Loading...</p>;
  }

  const { attempt, questions } = detail;
  const questionMap = new Map(questions.map((q) => [q._id, q]));
  const total = attempt.totalCorrect + attempt.totalWrong + attempt.totalSkipped;

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="card flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">{testName(attempt)}</h3>
          <p className="text-sm text-gray-500">
            {attempt.userId?.profile?.name || attempt.userId?.email
              ? `${attempt.userId.profile?.name || attempt.userId.email} · `
              : ''}
            {new Date(attempt.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">
            {attempt.score}/{total}
          </p>
          {attempt.passed === false ? (
            <span className="badge bg-danger-50 text-danger-700">Failed</span>
          ) : (
            <span className="badge bg-success-50 text-success-700">Passed</span>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {attempt.answers.map((ans, idx) => {
          const q = questionMap.get(ans.questionId);
          const selected = q?.options?.find((o) => o._id === ans.selectedOptionId);
          const correct = q?.options?.find((o) => o.isCorrect);
          const skipped = !ans.selectedOptionId;
          return (
            <div key={ans.questionId || idx} className="card">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium text-gray-900">
                  Q{idx + 1}. {q?.name || 'Question'}
                </p>
                {skipped ? (
                  <span className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <MinusCircle size={14} /> Skipped
                  </span>
                ) : ans.isCorrect ? (
                  <span className="flex items-center gap-1 text-xs text-success-700 shrink-0">
                    <CheckCircle2 size={14} /> Correct
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-danger-700 shrink-0">
                    <XCircle size={14} /> Wrong
                  </span>
                )}
              </div>
              {q?.category && (
                <p className="text-xs text-gray-400 mt-1">
                  {q.category}
                  {q.subcategory ? ` · ${q.subcategory}` : ''}
                  {q.difficulty ? ` · ${q.difficulty}` : ''}
                </p>
              )}
              {!skipped && (
                <p className="text-xs text-gray-600 mt-2">
                  Selected: <span className="font-medium">{selected?.text || '—'}</span>
                </p>
              )}
              {!ans.isCorrect && (
                <p className="text-xs text-gray-600 mt-1">
                  Correct answer: <span className="font-medium">{correct?.text || '—'}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
